import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { RefreshCw, Search, BarChart3, ChevronDown, ChevronRight } from 'lucide-react';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import LiveBadge from '../../components/LiveBadge';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'all', label: 'All Time' },
];

export default function GameRecordsPage() {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [providerPools, setProviderPools] = useState({});   // live upstream pool balances
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterProvider, setFilterProvider] = useState('all');
  const [period, setPeriod] = useState('week');
  const [expandedRow, setExpandedRow] = useState(null);
  const [providers, setProviders] = useState([]);

  // Initial + period-change fetch (shows spinner the first time only).
  const fetchAll = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    try {
      const [recRes, provRes] = await Promise.all([
        api.get(`/api/admin/game-records?period=${period}`).catch(() => ({ data: { records: [], summary: null, provider_pools: {} } })),
        api.get('/api/admin/providers').catch(() => ({ data: { data: { items: [] } } })),
      ]);
      setRecords(recRes.data.records || []);
      setSummary(recRes.data.summary || null);
      setProviderPools(recRes.data.provider_pools || {});
      setProviders(provRes.data.data?.items || provRes.data.items || []);
    } catch { /* non-fatal */ }
    finally { if (!silent) setLoading(false); }
  }, [period]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Lightweight live pool-balance poller — every 5s. Hits the live
  // upstream endpoint rather than the heavy listing so we don't
  // re-aggregate every account on every tick.
  useAutoRefresh(async () => {
    if (expandedRow !== null) return;   // pause while the admin is reading details
    try {
      // Pull live pool balances (authoritative)
      const live = await api.get('/api/admin/game-records/live-balances');
      setProviderPools(live.data?.pools || {});
      // Silent refresh of accounts list so new redeems / loads appear
      fetchAll({ silent: true });
    } catch { /* non-fatal */ }
  }, 5000, expandedRow === null);

  const syncRecords = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/api/admin/game-records/sync');
      toast.success(`Synced! ${res.data.synced_accounts || 0} balances, ${res.data.records_fetched || 0} game records`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const filtered = records.filter(r => {
    if (filterProvider !== 'all' && r.provider_code !== filterProvider) return false;
    if (search) {
      const s = search.toLowerCase();
      return (r.username || '').toLowerCase().includes(s)
          || (r.player_email || '').toLowerCase().includes(s)
          || (r.player_username || '').toLowerCase().includes(s);
    }
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5" data-testid="game-records-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-3">Game Records <LiveBadge /></h2>
          <p className="text-sm text-gray-400">Players' activity across providers & inner games (fish, slots, etc.) — your main-platform players only.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 mr-1">Period:</span>
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${
                period === p.key ? 'bg-[#D4AF37] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`game-records-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          <Button onClick={syncRecords} disabled={syncing} className="ml-2 bg-[#D4AF37] text-black" data-testid="sync-records-btn">
            {syncing ? <div className="spinner w-4 h-4 mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Sync All
          </Button>
        </div>
      </div>

      {/* Summary KPIs */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="game-records-summary">
          <SummaryCard label="Total Accounts" value={summary.total_accounts} />
          <SummaryCard label="Stored Balance ($)" value={`$${(summary.total_balance || 0).toFixed(2)}`} gold hint="sum of last-known per-player balances" />
          <SummaryCard label={`Loaded — ${PERIODS.find(p => p.key === period)?.label || 'All'}`} value={`$${(summary.total_loaded || 0).toFixed(2)}`} color="text-green-400" />
          <SummaryCard label={`Redeemed — ${PERIODS.find(p => p.key === period)?.label || 'All'}`} value={`$${(summary.total_redeemed || 0).toFixed(2)}`} color="text-red-400" />
        </div>
      )}

      {/* Per-Provider cards — now with LIVE pool balance */}
      {summary?.per_provider && Object.keys(summary.per_provider).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#D4AF37]" /> Spend & Live Pool by Provider</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="game-records-provider-cards">
            {Object.entries(summary.per_provider).map(([code, data]) => {
              const pool = providerPools[code];
              return (
                <div key={code} className="bg-[#131822] rounded-xl border border-white/10 p-4" data-testid={`provider-card-${code}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold capitalize text-[#D4AF37]">{code}</h4>
                    <span className="text-xs text-gray-500">{data.accounts} players</span>
                  </div>

                  {/* Live upstream pool balance — the authoritative number */}
                  <div
                    className="mb-3 rounded-lg p-2.5 border"
                    style={{
                      background: pool?.ok === false ? 'rgba(239,68,68,0.07)' : 'rgba(34,197,94,0.07)',
                      borderColor: pool?.ok === false ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)',
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Live Pool Balance</span>
                      <LiveBadge label="Live" />
                    </div>
                    {pool?.ok === false ? (
                      <p className="text-xs text-red-400 mt-1 truncate" title={pool.error}>Provider unavailable</p>
                    ) : pool?.pool_balance_usd != null ? (
                      <p className="text-xl font-bold text-green-400 mt-0.5">${Number(pool.pool_balance_usd).toFixed(2)}</p>
                    ) : (
                      <p className="text-sm text-gray-500 mt-0.5">Loading…</p>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><p className="text-gray-500">Player Balances</p><p className="text-[#D4AF37] font-bold">${(data.balance || 0).toFixed(2)}</p></div>
                    <div><p className="text-gray-500">Loaded</p><p className="text-green-400 font-bold">${(data.loaded || 0).toFixed(2)}</p></div>
                    <div><p className="text-gray-500">Redeemed</p><p className="text-red-400 font-bold">${(data.redeemed || 0).toFixed(2)}</p></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters — NO tenant filter (main admin only sees main-scope players) */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search player, username..." className="pl-10 bg-[#131822] border-white/10" data-testid="game-records-search" />
        </div>
        <select value={filterProvider} onChange={e => setFilterProvider(e.target.value)} className="bg-[#131822] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="game-records-provider-filter">
          <option value="all">All Providers</option>
          {providers.map(p => <option key={p.id} value={p.code}>{p.name}</option>)}
        </select>
      </div>

      {/* Records Table */}
      <div className="bg-[#131822] rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="game-records-table">
          <thead>
            <tr className="border-b border-white/5 text-xs text-gray-500">
              <th className="w-8 p-3"></th>
              <th className="text-left p-3">Player</th>
              <th className="text-left p-3">Provider</th>
              <th className="text-left p-3">Game Username</th>
              <th className="text-right p-3">Balance ($)</th>
              <th className="text-right p-3">Loaded ($)</th>
              <th className="text-right p-3">Redeemed ($)</th>
              <th className="text-left p-3">Last Synced</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <React.Fragment key={r.account_id}>
                <tr
                  className="border-b border-white/[0.03] hover:bg-white/[0.02] cursor-pointer"
                  onClick={() => setExpandedRow(expandedRow === r.account_id ? null : r.account_id)}
                >
                  <td className="p-3 text-gray-500">
                    {r.inner_records
                      ? (expandedRow === r.account_id ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)
                      : <span className="w-3.5 inline-block" />}
                  </td>
                  <td className="p-3"><span className="text-white">{r.player_email || r.player_username || '?'}</span></td>
                  <td className="p-3"><span className="text-[#D4AF37] capitalize font-medium">{r.provider_code}</span></td>
                  <td className="p-3 font-mono text-xs">{r.username}</td>
                  <td className="p-3 text-right font-semibold text-[#D4AF37]">${(r.balance || 0).toFixed(2)}</td>
                  <td className="p-3 text-right text-green-400">${(r.total_loaded || 0).toFixed(2)}</td>
                  <td className="p-3 text-right text-red-400">${(r.total_redeemed || 0).toFixed(2)}</td>
                  <td className="p-3 text-xs text-gray-500">
                    {r.last_synced ? new Date(r.last_synced).toLocaleString() : '—'}
                  </td>
                </tr>

                {/* Expanded: Inner Game Records */}
                {expandedRow === r.account_id && (
                  <tr><td colSpan={8} className="p-0">
                    <div className="bg-[#0B0E14] px-6 py-4 border-y border-white/5">
                      <h4 className="text-xs font-semibold text-[#D4AF37] mb-3">Inner Game Activity — {r.provider_code} / {r.username}</h4>
                      {r.inner_records?.summary && (
                        <div className="grid grid-cols-4 gap-3 mb-3 text-xs">
                          <div className="bg-[#131822] rounded-lg p-2.5"><p className="text-gray-500">Total Played</p><p className="text-white font-bold">${(r.inner_records.summary.sum_played || 0).toFixed(2)}</p></div>
                          <div className="bg-[#131822] rounded-lg p-2.5"><p className="text-gray-500">Total Won</p><p className="text-green-400 font-bold">${(r.inner_records.summary.sum_win || 0).toFixed(2)}</p></div>
                          <div className="bg-[#131822] rounded-lg p-2.5"><p className="text-gray-500">Net Win/Loss</p><p className={`font-bold ${(r.inner_records.summary.sum_win_lose || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>${(r.inner_records.summary.sum_win_lose || 0).toFixed(2)}</p></div>
                          <div className="bg-[#131822] rounded-lg p-2.5"><p className="text-gray-500">Records</p><p className="text-white font-bold">{r.inner_records.count || 0}</p></div>
                        </div>
                      )}
                      {r.inner_records?.records?.length > 0 ? (
                        <div className="bg-[#131822] rounded-lg overflow-hidden">
                          <table className="w-full text-xs">
                            <thead><tr className="text-gray-500 border-b border-white/5">
                              <th className="text-left p-2">Game ID</th>
                              <th className="text-left p-2">Account</th>
                              <th className="text-left p-2">Game Title</th>
                              <th className="text-right p-2">Bal Before</th>
                              <th className="text-right p-2">Total Played</th>
                              <th className="text-right p-2">Total Win</th>
                              <th className="text-right p-2">Bal Now</th>
                              <th className="text-left p-2">Time</th>
                            </tr></thead>
                            <tbody>
                              {r.inner_records.records.slice(0, 30).map((gr, gi) => (
                                <tr key={gi} className="border-b border-white/[0.03]">
                                  <td className="p-2 font-mono text-gray-400">{gr.game_id || gr.id || '-'}</td>
                                  <td className="p-2">{gr.account || gr.user_id || r.username}</td>
                                  <td className="p-2 text-white font-medium">{gr.game_name || gr.game_title || gr.game_type || 'Unknown'}</td>
                                  <td className="p-2 text-right text-gray-400">${(gr.balance_before || gr.start_balance || 0).toFixed(2)}</td>
                                  <td className="p-2 text-right text-orange-400">${(gr.total_played || gr.played || gr.bet || 0).toFixed(2)}</td>
                                  <td className="p-2 text-right text-green-400">${(gr.total_win || gr.win || gr.won || 0).toFixed(2)}</td>
                                  <td className="p-2 text-right text-[#D4AF37] font-semibold">${(gr.balance_now || gr.end_balance || gr.balance || 0).toFixed(2)}</td>
                                  <td className="p-2 text-gray-500">{gr.recording_time || gr.created_at || gr.timestamp || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500 text-xs">
                          {r.inner_records ? 'No inner game records for this player yet.' : 'Click "Sync All" to fetch inner game data from the provider.'}
                        </div>
                      )}
                      {r.inner_records?.fetched_at && (
                        <p className="text-[10px] text-gray-600 mt-2">Last fetched: {new Date(r.inner_records.fetched_at).toLocaleString()}</p>
                      )}
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-8 text-gray-500" data-testid="game-records-empty">
            No records. Click "Sync All" to fetch from providers.
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, gold, color, hint }) {
  return (
    <div className="bg-[#131822] rounded-xl border border-white/10 p-4">
      <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider font-semibold">{label}</p>
      <p className={`text-lg font-bold ${gold ? 'text-[#D4AF37]' : color || 'text-white'}`}>{value}</p>
      {hint && <p className="text-[9px] text-gray-600 mt-0.5">{hint}</p>}
    </div>
  );
}
